import maplibregl from "maplibre-gl"

import Hook from "../../utils/hook"
import { getCssCustomProperties } from "../../utils/custom-properties"
import {
  isTouch,
  mapCardDrag,
  mapCardDragActions,
} from "../../utils/map-card-drag"

const POINTS_SOURCE_ID = `regions-source`
const POINTS_LAYER_ID = `regions-layer`
const BOUNDARIES_SOURCE_ID = `regions-boundaries-source`
const BOUNDARIES_FILL_LAYER_ID = `regions-boundaries-fill`
const BOUNDARIES_LINE_LAYER_ID = `regions-boundaries-line`
const HOVERED_STATE = `regionsHoveredFeatureId`

// the regions page: its map (in the map view) draws the regions as their
// boundaries (loaded once by MapLibre from the cacheable `mapBoundaries` URL,
// see `NaturkartanWeb.RegionBoundaries`) and markers; `mapData` lists the
// regions to show, so filtering changes just the markers and the boundaries
// filter. Hovering a region's card highlights it on the map and vice versa.
// On touchscreens a tapped region's card shows above the page (`mapCard`),
// dragged down it's dismissed (see `utils/map-card-drag.js`)
export const RegionsPage = Hook(`RegionsPage`, {
  ...mapCardDrag(`--regions-page-map-card`),
  values: {
    mapData: Array,
    mapBoundaries: String,
    cardHighlightedClass: String,
    mapCardDraggingClass: String,
    mapCardDraggedAwayClass: String,
  },
  refs: [`maplibre`],
  targets: [`card`, `mapCard`],
  actions: [
    { name: `cardEnter`, targets: `card`, type: `mouseenter` },
    { name: `cardLeave`, targets: `card`, type: `mouseleave` },
    { name: `cardEnter`, targets: `card`, type: `focusin` },
    { name: `cardLeave`, targets: `card`, type: `focusout` },
    ...mapCardDragActions(`mapCard`),
  ],
  mapLoaded: false,
  hoveredFeatureId: null,
  highlightedCard: null,
  mouseMoveHandler: null,
  mouseLeaveHandler: null,
  clickHandler: null,
  handledLayerIds: null,
  popup: null,
  popupAbortController: null,
  mapCardRegionId: null,

  mounted() {
    this.layerIds = new Set()
    this.sourceIds = new Set()
    this.regionsById = new Map()
    this.regionIdsByCardId = new Map()
    this.cssCustomProps = getCssCustomProperties([
      `--color-sea-green`,
      `--color-eagle-green`,
      `--color-alloy-orange`,
    ])
    this.indexRegions()
  },

  destroyed() {
    this.clear()
  },

  maplibreRefMounted(maplibreRef) {
    this.mapLoaded = Boolean(maplibreRef.map?.isStyleLoaded?.())

    if (this.mapLoaded) {
      this.draw()
    } else {
      maplibreRef.addLoadHandler(() => {
        this.mapLoaded = true
        this.draw()
      })
    }
  },

  // the map is about to be removed (e.g. back to the grid view)
  maplibreRefDestroyed() {
    this.removePopup()
    this.mapLoaded = false
    this.reset()
  },

  // whether the map has loaded once; `isStyleLoaded()` isn't used for this as
  // it's false again whenever a source is loading (e.g. tiles while panning)
  isMapLibreReady() {
    return Boolean(this.maplibreRef?.map && this.mapLoaded)
  },

  mapDataValueChanged() {
    this.update()
  },

  mapBoundariesValueChanged() {
    this.draw()
  },

  cardEnterAction(e, node) {
    const regionId = this.regionIdsByCardId.get(node.id)
    if (regionId != null) this.setMapHovered(regionId)
  },

  cardLeaveAction() {
    this.setMapHovered(this.hoveredFeatureId ?? -1)
  },

  cardTargetDestroyed(card) {
    if (card === this.highlightedCard) this.highlightedCard = null
  },

  // a new card (another region's card patches the shown one in place)
  mapCardTargetMounted() {
    this.mapCardDragReset()
  },

  mapCardHide() {
    this.pushEvent(`hide_region_card`, { id: this.mapCardRegionId })
  },

  setMapHovered(regionId) {
    if (this.isMapLibreReady())
      this.maplibreRef.map.setGlobalStateProperty(HOVERED_STATE, regionId)
  },

  highlightCard(cardId) {
    const card = cardId && this.cardTargets?.find(({ id }) => id === cardId)
    if (card === this.highlightedCard) return

    if (this.highlightedCard)
      this.classRemove(this.cardHighlightedClassValue, this.highlightedCard)
    if (card) this.classAdd(this.cardHighlightedClassValue, card)
    this.highlightedCard = card || null
  },

  draw() {
    this.indexRegions()
    if (!this.isMapLibreReady()) return
    this.clear()
    this.addSourcesAndLayers()
    this.addMapHandlers()
  },

  // shows the current regions without reloading the boundaries
  update() {
    if (!this.isMapLibreReady() || !this.sourceIds.has(POINTS_SOURCE_ID)) {
      this.draw()
      return
    }

    const map = this.maplibreRef.map
    this.indexRegions()
    map.getSource(POINTS_SOURCE_ID)?.setData(this.pointsData())

    ;[BOUNDARIES_FILL_LAYER_ID, BOUNDARIES_LINE_LAYER_ID].forEach((id) => {
      if (map.getLayer(id)) map.setFilter(id, this.regionsFilter())
    })
  },

  regions() {
    return this.mapDataValue || []
  },

  indexRegions() {
    const regions = this.regions()
    this.regionsById = new Map(regions.map((region) => [region.id, region]))
    this.regionIdsByCardId = new Map(
      regions.map(({ id, card_id: cardId }) => [cardId, id])
    )
  },

  regionsFilter() {
    return [`in`, [`id`], [`literal`, Array.from(this.regionsById.keys())]]
  },

  pointsData() {
    const features = this.regions()
      .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng))
      .map(({ id, title, lat, lng, path }) => ({
        type: `Feature`,
        id,
        geometry: {
          type: `Point`,
          coordinates: [lng, lat],
        },
        properties: { id, title, path },
      }))

    return { type: `FeatureCollection`, features }
  },

  addSourcesAndLayers() {
    if (!this.maplibreRef) return

    const map = this.maplibreRef.map
    const hovered = [`==`, [`id`], [`global-state`, HOVERED_STATE]]

    if (this.mapBoundariesValue) {
      map.addSource(BOUNDARIES_SOURCE_ID, {
        type: `geojson`,
        data: this.mapBoundariesValue,
      })
      this.sourceIds.add(BOUNDARIES_SOURCE_ID)

      map.addLayer({
        id: BOUNDARIES_FILL_LAYER_ID,
        source: BOUNDARIES_SOURCE_ID,
        type: `fill`,
        filter: this.regionsFilter(),
        paint: {
          "fill-color": [
            `case`,
            hovered,
            this.cssCustomProps[`--color-alloy-orange`],
            this.cssCustomProps[`--color-sea-green`],
          ],
          "fill-opacity": [`case`, hovered, 0.35, 0.12],
        },
      })
      this.layerIds.add(BOUNDARIES_FILL_LAYER_ID)

      map.addLayer({
        id: BOUNDARIES_LINE_LAYER_ID,
        source: BOUNDARIES_SOURCE_ID,
        type: `line`,
        filter: this.regionsFilter(),
        paint: {
          "line-color": [
            `case`,
            hovered,
            this.cssCustomProps[`--color-alloy-orange`],
            this.cssCustomProps[`--color-eagle-green`],
          ],
          "line-width": [`case`, hovered, 2, 1],
          "line-opacity": [`case`, hovered, 0.9, 0.4],
        },
      })
      this.layerIds.add(BOUNDARIES_LINE_LAYER_ID)
    }

    map.addSource(POINTS_SOURCE_ID, {
      type: `geojson`,
      data: this.pointsData(),
    })
    this.sourceIds.add(POINTS_SOURCE_ID)

    map.addLayer({
      id: POINTS_LAYER_ID,
      source: POINTS_SOURCE_ID,
      type: `symbol`,
      layout: {
        "icon-image": [`case`, hovered, `none-active`, `none`],
        "icon-size": 0.5,
        "icon-allow-overlap": true,
        "icon-anchor": `bottom`,
        "symbol-z-order": `source`,
        "symbol-sort-key": [`case`, hovered, 1, 0],
      },
    })
    this.layerIds.add(POINTS_LAYER_ID)
  },

  interactiveLayerIds() {
    return [POINTS_LAYER_ID, BOUNDARIES_FILL_LAYER_ID].filter((id) =>
      this.layerIds.has(id)
    )
  },

  addMapHandlers() {
    if (!this.maplibreRef) return

    const map = this.maplibreRef.map
    const layerIds = this.interactiveLayerIds()

    // a tap shows the region's card (the server renders it, as its card in
    // the grid may not be loaded yet), a mouse click opens the region
    this.clickHandler = (event) => {
      const region = this.regionsById.get(event.features?.[0]?.id)
      if (!region) return

      if (isTouch(event.originalEvent)) {
        this.removePopup()
        this.mapCardRegionId = region.id
        this.pushEvent(`show_region_card`, { id: region.id })
      } else if (region.path) {
        window.location.href = region.path
      }
    }

    this.mouseMoveHandler = (event) => {
      const featureId = event.features?.[0]?.id
      if (featureId == null || featureId === this.hoveredFeatureId) return

      const region = this.regionsById.get(featureId)
      if (!region) return

      this.hoveredFeatureId = featureId
      this.setMapHovered(featureId)
      this.highlightCard(region.card_id)
      map.getCanvas().style.cursor = `pointer`

      // over the region's marker, or the pointer when it has no position
      const hasPosition =
        Number.isFinite(region.lat) && Number.isFinite(region.lng)

      this.removePopup()
      this.popup = new maplibregl.Popup({
        offset: hasPosition ? 30 : 10,
        anchor: `bottom`,
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat(hasPosition ? [region.lng, region.lat] : event.lngLat)
        .setText(region.title)
        .addTo(map)

      this.keepHoveredOverPopup(map, layerIds)
    }

    this.mouseLeaveHandler = (event) => {
      // onto the title popup: the region stays hovered (see
      // `keepHoveredOverPopup`), so the popup doesn't flicker
      const popupElement = this.popup?.getElement()
      if (popupElement?.contains(event?.originalEvent?.relatedTarget)) return

      this.hoveredFeatureId = null
      this.setMapHovered(-1)
      this.highlightCard(null)
      map.getCanvas().style.cursor = ``
      this.removePopup()
    }

    this.handledLayerIds = layerIds
    map.on(`mousemove`, layerIds, this.mouseMoveHandler)
    map.on(`mouseleave`, layerIds, this.mouseLeaveHandler)
    map.on(`click`, layerIds, this.clickHandler)
  },

  // the popup covers the map, so the pointer moving onto it leaves the
  // region's layer; it stays hovered until the pointer leaves the popup for
  // somewhere off the region (back onto it, the layer's events take over)
  keepHoveredOverPopup(map, layerIds) {
    this.popupAbortController = new AbortController()

    this.popup.getElement().addEventListener(
      `mouseleave`,
      (e) => {
        const canvas = map.getCanvas()

        if (e.relatedTarget === canvas) {
          const { left, top } = canvas.getBoundingClientRect()
          const features = map.queryRenderedFeatures(
            [e.clientX - left, e.clientY - top],
            { layers: layerIds }
          )
          if (features[0]?.id === this.hoveredFeatureId) return
        }

        this.mouseLeaveHandler?.()
      },
      { signal: this.popupAbortController.signal }
    )
  },

  removePopup() {
    this.popupAbortController?.abort()
    this.popupAbortController = null
    this.popup?.remove()
    this.popup = null
  },

  clear() {
    let map = null
    if (this.maplibreRef) map = this.maplibreRef.map

    if (map) {
      map.getCanvas().style.cursor = ``

      if (this.handledLayerIds) {
        if (this.mouseMoveHandler)
          map.off(`mousemove`, this.handledLayerIds, this.mouseMoveHandler)

        if (this.mouseLeaveHandler)
          map.off(`mouseleave`, this.handledLayerIds, this.mouseLeaveHandler)

        if (this.clickHandler)
          map.off(`click`, this.handledLayerIds, this.clickHandler)
      }
    }

    if (this.isMapLibreReady()) {
      map.setGlobalStateProperty(HOVERED_STATE, -1)

      Array.from(this.layerIds).forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id)
      })

      Array.from(this.sourceIds).forEach((id) => {
        if (map.getSource(id)) map.removeSource(id)
      })
    }

    this.removePopup()
    this.reset()
  },

  reset() {
    this.layerIds.clear()
    this.sourceIds.clear()
    this.highlightCard(null)
    this.popup = null
    this.hoveredFeatureId = null
    this.handledLayerIds = null
    this.mouseMoveHandler = null
    this.mouseLeaveHandler = null
    this.clickHandler = null
  },
})
